import React, { useState, useRef, useMemo, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/api/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { DEFECT_TYPES, DEFECT_TYPE_COLORS, DEFECT_STATUS, SEVERITY, WARRANTY_FAMILIES, WARRANTY_FAMILY_KEYS, WARRANTY_TYPES, formatEUR } from "@/lib/dict";
import { Map, MapPin, X, Trash2, Upload, Download, Plus, Palette, Filter as FilterIcon, Move } from "lucide-react";
import { toast } from "sonner";
import PlanRenderer from "@/components/PlanRenderer";
import { exportPlanAsPdf } from "@/lib/pdfExport";

export default function Plans() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: plans = [] } = useQuery({ queryKey: ["plans-all"], queryFn: () => api.get("/plans").then((r) => r.data) });
  const { data: assets = [] } = useQuery({ queryKey: ["assets"], queryFn: () => api.get("/assets").then((r) => r.data) });
  const [uploadOpen, setUploadOpen] = useState(false);
  const [pForm, setPForm] = useState({ name: "", asset_id: "", building: "", floor: "" });
  const [pFile, setPFile] = useState(null);

  const uploadPlan = async () => {
    if (!pFile || !pForm.name || !pForm.asset_id) return toast.error("Nom, actif et fichier requis");
    try {
      const fd = new FormData();
      fd.append("file", pFile);
      const { data: doc } = await api.post(`/upload?asset_id=${pForm.asset_id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/plans", { ...pForm, file_url: doc.url, is_image: !pFile.name.toLowerCase().endsWith(".pdf") });
      qc.invalidateQueries({ queryKey: ["plans-all"] });
      toast.success("Plan téléversé");
      setUploadOpen(false); setPForm({ name: "", asset_id: "", building: "", floor: "" }); setPFile(null);
    } catch (e) {
      toast.error("Échec de l'upload");
    }
  };

  if (!id) {
    return (
      <div className="space-y-6">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Module</div>
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mt-1">Plans interactifs</h1>
            <p className="text-sm text-slate-500 mt-1.5">Téléversez des PDF ou images et annotez avec des marqueurs colorés.</p>
          </div>
          <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
            <DialogTrigger asChild>
              <Button data-testid="upload-plan-btn" className="text-white" style={{ background: "#023E73" }}><Plus size={16} className="mr-1.5" /> Nouveau plan</Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader><DialogTitle>Téléverser un plan PDF ou image</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Nom du plan</Label><Input data-testid="plan-name" value={pForm.name} onChange={(e) => setPForm({ ...pForm, name: e.target.value })} placeholder="Ex: Plan RDC bâtiment A" /></div>
                <div><Label>Actif</Label>
                  <Select value={pForm.asset_id} onValueChange={(v) => setPForm({ ...pForm, asset_id: v })}>
                    <SelectTrigger data-testid="plan-asset"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                    <SelectContent>{assets.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Bâtiment</Label><Input value={pForm.building} onChange={(e) => setPForm({ ...pForm, building: e.target.value })} /></div>
                  <div><Label>Étage</Label><Input value={pForm.floor} onChange={(e) => setPForm({ ...pForm, floor: e.target.value })} /></div>
                </div>
                <div>
                  <Label>Fichier (PDF ou image)</Label>
                  <input type="file" data-testid="plan-file" accept="application/pdf,image/*" onChange={(e) => setPFile(e.target.files?.[0])} className="text-sm block mt-1" />
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="plan-upload-submit" onClick={uploadPlan} className="text-white" style={{ background: "#023E73" }}><Upload size={14} className="mr-1.5" /> Téléverser</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {plans.map((p) => (
            <Card key={p.id} data-testid={`plan-card-${p.id}`} onClick={() => navigate(`/plans/${p.id}`)} className="overflow-hidden cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all">
              <div className="aspect-video bg-slate-100 overflow-hidden flex items-center justify-center">
                {p.file_url?.toLowerCase().endsWith(".pdf") || p.file_url?.startsWith("/api/files/") ? (
                  <div className="flex flex-col items-center text-slate-500"><Map size={32} /><div className="text-xs mt-1 uppercase tracking-wider font-semibold">PDF</div></div>
                ) : (
                  <img src={p.file_url} alt={p.name} className="w-full h-full object-cover" />
                )}
              </div>
              <div className="p-4"><div className="font-display font-semibold">{p.name}</div><div className="text-xs text-slate-500 mt-1">{p.building || "—"} • {p.floor || "—"}</div></div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return <PlanViewer id={id} />;
}

function PlanViewer({ id }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [placingMode, setPlacingMode] = useState(false);
  const [colorMode, setColorMode] = useState(() => localStorage.getItem("wrm_plan_color_mode") || "warranty"); // warranty | type
  const [warrantyFilter, setWarrantyFilter] = useState("all");
  const [pendingClick, setPendingClick] = useState(null); // {x, y, page} — opens the place dialog
  const [placeDialogOpen, setPlaceDialogOpen] = useState(false);
  const [placeTab, setPlaceTab] = useState("defect");
  const [pickDefectId, setPickDefectId] = useState("");
  const [pickClaimId, setPickClaimId] = useState("");
  const [quickForm, setQuickForm] = useState({ title: "", description: "", type: "infiltration", severity: "minor", warranty_type: "none", estimated_cost: 0 });
  const [dragging, setDragging] = useState(null); // {marker, x, y}
  const [currentPage, setCurrentPage] = useState(1);
  const containerRef = useRef(null);
  const exportRef = useRef(null);

  const setMode = (m) => { setColorMode(m); localStorage.setItem("wrm_plan_color_mode", m); };

  const { data } = useQuery({ queryKey: ["plan", id], queryFn: () => api.get(`/plans/${id}`).then((r) => r.data) });
  const { data: defects = [] } = useQuery({ queryKey: ["defects-all"], queryFn: () => api.get("/defects").then((r) => r.data) });
  const { data: claims = [] } = useQuery({ queryKey: ["claims-all"], queryFn: () => api.get("/claims").then((r) => r.data) });

  const create = useMutation({
    mutationFn: (m) => api.post(`/plans/${id}/markers`, m),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["plan", id] }); toast.success("Marqueur ajouté"); },
  });
  const move = useMutation({
    mutationFn: ({ mid, x, y }) => api.patch(`/plans/markers/${mid}`, { x, y }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["plan", id] }); },
  });
  const remove = useMutation({
    mutationFn: (mid) => api.delete(`/plans/markers/${mid}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["plan", id] }); toast.success("Marqueur supprimé"); setSelectedMarker(null); },
  });
  const createDefectQuick = useMutation({
    mutationFn: (payload) => api.post("/defects", payload),
  });

  const defectMap = useMemo(() => Object.fromEntries(defects.map((d) => [d.id, d])), [defects]);
  const claimMap = useMemo(() => Object.fromEntries(claims.map((c) => [c.id, c])), [claims]);

  // Compute the color for a marker based on the active mode; fallback to the stored color when the linked defect is missing.
  const markerColor = (m) => {
    if (colorMode === "warranty") {
      const linkedDefect = m.linked_type === "defect" ? defectMap[m.defect_id] : null;
      const wk = (linkedDefect?.warranty_type) || "none";
      return WARRANTY_FAMILIES[wk]?.color || "#6B7280";
    }
    const linkedDefect = m.linked_type === "defect" ? defectMap[m.defect_id] : null;
    return DEFECT_TYPE_COLORS[linkedDefect?.type] || m.color || "#023E73";
  };

  // Drag state kept in a ref to avoid re-running the mutation from within a state updater (React dev double-invokes updaters).
  const draggingRef = useRef(null);
  useEffect(() => { draggingRef.current = dragging; }, [dragging]);

  // Drag effect must be declared before any early return so hooks stay in a stable order.
  useEffect(() => {
    if (!dragging) return;
    const onMove = (ev) => {
      const box = containerRef.current;
      if (!box) return;
      const rect = box.getBoundingClientRect();
      const x = Math.min(100, Math.max(0, ((ev.clientX - rect.left) / rect.width) * 100));
      const y = Math.min(100, Math.max(0, ((ev.clientY - rect.top) / rect.height) * 100));
      setDragging((prev) => prev ? { ...prev, x, y } : prev);
    };
    const onUp = () => {
      // Read the final drag state via ref and clear it, THEN mutate — never side-effect inside a setState updater.
      const prev = draggingRef.current;
      setDragging(null);
      if (prev) {
        const { marker, x, y } = prev;
        if (Math.abs(x - marker.x) > 0.1 || Math.abs(y - marker.y) > 0.1) {
          move.mutate({ mid: marker.id, x, y });
          toast.success("Marqueur repositionné");
        }
      }
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
  }, [dragging, move]);

  if (!data) return <div className="text-slate-500">Chargement…</div>;
  const { plan, markers } = data;

  // Asset-scoped defects / claims for the selection dialog (only entities of this plan's asset).
  const assetDefects = defects.filter((d) => d.asset_id === plan.asset_id);
  const assetClaims = claims.filter((c) => c.asset_id === plan.asset_id);

  // Filter markers by warranty family AND by current PDF page (images always show page 1 markers).
  const visibleMarkers = markers.filter((m) => {
    if ((m.page || 1) !== currentPage) return false;
    if (warrantyFilter === "all") return true;
    if (m.linked_type !== "defect") return warrantyFilter === "none";
    const wk = defectMap[m.defect_id]?.warranty_type || "none";
    return wk === warrantyFilter;
  });

  // Compute a click's plan-relative position (0-100 %). Uses containerRef which now sizes to the actual image/canvas.
  const clickToPercent = (e) => {
    const box = containerRef.current;
    if (!box) return null;
    const rect = box.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    if (x < 0 || x > 100 || y < 0 || y > 100) return null;
    const page = parseInt(box.getAttribute("data-plan-page") || "1", 10);
    return { x, y, page };
  };

  const handlePlanClick = (e) => {
    if (!placingMode) return;
    const pos = clickToPercent(e);
    if (!pos) return;
    setPendingClick(pos);
    setPickDefectId(assetDefects[0]?.id || "");
    setPickClaimId(assetClaims[0]?.id || "");
    setQuickForm({ title: "", description: "", type: "infiltration", severity: "minor", warranty_type: "none", estimated_cost: 0 });
    setPlaceTab(assetDefects.length ? "defect" : (assetClaims.length ? "claim" : "new"));
    setPlaceDialogOpen(true);
  };

  const closePlaceDialog = () => { setPlaceDialogOpen(false); setPendingClick(null); };

  const confirmPlace = async () => {
    if (!pendingClick) return;
    const { x, y, page } = pendingClick;
    try {
      if (placeTab === "defect") {
        if (!pickDefectId) return toast.error("Choisissez un désordre");
        const d = defectMap[pickDefectId];
        const wk = d?.warranty_type || "none";
        const color = colorMode === "warranty" ? WARRANTY_FAMILIES[wk].color : (DEFECT_TYPE_COLORS[d?.type] || "#023E73");
        await create.mutateAsync({ plan_id: id, linked_type: "defect", defect_id: pickDefectId, x, y, page, color, status: d?.status });
      } else if (placeTab === "claim") {
        if (!pickClaimId) return toast.error("Choisissez un sinistre");
        const c = claimMap[pickClaimId];
        const color = c?.claim_family === "DO" ? WARRANTY_FAMILIES.DO.color : "#3B82F6";
        await create.mutateAsync({ plan_id: id, linked_type: "claim", claim_id: pickClaimId, x, y, page, color, status: c?.status });
      } else {
        // Quick new defect
        if (!quickForm.title.trim()) return toast.error("Titre requis");
        const created = await createDefectQuick.mutateAsync({
          ...quickForm,
          asset_id: plan.asset_id,
          building: plan.building || null,
          floor: plan.floor || null,
          warranty_type: quickForm.warranty_type === "none" ? null : quickForm.warranty_type,
        });
        const nd = created.data;
        const wk = nd.warranty_type || "none";
        const color = colorMode === "warranty" ? WARRANTY_FAMILIES[wk].color : (DEFECT_TYPE_COLORS[nd.type] || "#023E73");
        await create.mutateAsync({ plan_id: id, linked_type: "defect", defect_id: nd.id, x, y, page, color, status: nd.status });
        qc.invalidateQueries({ queryKey: ["defects-all"] });
        qc.invalidateQueries({ queryKey: ["defects"] });
      }
      closePlaceDialog();
      setPlacingMode(false);
    } catch (e) {
      toast.error("Échec de la création du marqueur");
    }
  };

  // Drag-to-move a marker
  const handleMarkerMouseDown = (e, m) => {
    if (placingMode) return; // don't start drag during placement mode
    e.preventDefault(); e.stopPropagation();
    setDragging({ marker: m, x: m.x, y: m.y });
  };

  // The markers overlay (rendered as children of PlanRenderer so it shares the same box as the image/canvas)
  const markersOverlay = (
    <>
      {visibleMarkers.map((m) => {
        const isDrag = dragging?.marker?.id === m.id;
        const x = isDrag ? dragging.x : m.x;
        const y = isDrag ? dragging.y : m.y;
        return (
          <button
            key={m.id}
            data-testid={`marker-${m.id}`}
            onClick={(e) => { e.stopPropagation(); if (!isDrag) setSelectedMarker(m); }}
            onMouseDown={(e) => handleMarkerMouseDown(e, m)}
            className={`absolute -translate-x-1/2 -translate-y-1/2 w-7 h-7 rounded-full border-2 border-white shadow-lg flex items-center justify-center text-white text-[10px] font-bold pointer-events-auto ${isDrag ? "cursor-grabbing scale-125" : "cursor-grab hover:scale-110"} transition`}
            style={{ left: `${x}%`, top: `${y}%`, background: markerColor(m) }}
            title={defectMap[m.defect_id]?.title || claimMap[m.claim_id]?.title || "Glisser pour repositionner"}
          >{m.linked_type === "defect" ? "D" : "S"}</button>
        );
      })}
    </>
  );

  return (
    <div className="space-y-4">
      <button onClick={() => navigate("/plans")} className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"><X size={14} /> Fermer le plan</button>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight">{plan.name}</h1>
          <p className="text-sm text-slate-500">
            {visibleMarkers.length}/{markers.length} marqueur(s){warrantyFilter !== "all" ? ` filtrés (${WARRANTY_FAMILIES[warrantyFilter]?.label})` : ""} — {placingMode ? "Cliquez sur le plan pour ouvrir la fenêtre de sélection." : "Placez un marqueur ou glissez-déposez pour repositionner."}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Color-mode toggle */}
          <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5" data-testid="color-mode-toggle">
            <button data-testid="color-mode-warranty" onClick={() => setMode("warranty")} className={`px-2.5 py-1 rounded text-xs flex items-center gap-1.5 font-semibold ${colorMode === "warranty" ? "text-white" : "text-slate-600"}`} style={colorMode === "warranty" ? { background: "#023E73" } : {}} title="Colorer par famille de garantie"><Palette size={13} /> Garantie</button>
            <button data-testid="color-mode-type" onClick={() => setMode("type")} className={`px-2.5 py-1 rounded text-xs flex items-center gap-1.5 font-semibold ${colorMode === "type" ? "text-white" : "text-slate-600"}`} style={colorMode === "type" ? { background: "#023E73" } : {}} title="Colorer par type de désordre"><Palette size={13} /> Type</button>
          </div>
          {/* Warranty family filter */}
          <div className="inline-flex items-center gap-1.5">
            <FilterIcon size={13} className="text-slate-400" />
            <Select value={warrantyFilter} onValueChange={setWarrantyFilter}>
              <SelectTrigger className="h-9 w-[170px] text-xs" data-testid="plan-warranty-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes garanties</SelectItem>
                {WARRANTY_FAMILY_KEYS.map((k) => (
                  <SelectItem key={k} value={k}>
                    <span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full inline-block" style={{ background: WARRANTY_FAMILIES[k].color }} />{WARRANTY_FAMILIES[k].full}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button data-testid="toggle-placing" variant={placingMode ? "default" : "outline"} onClick={() => setPlacingMode(!placingMode)} className={placingMode ? "text-white" : ""} style={placingMode ? { background: "#023E73" } : {}}>
            <MapPin size={16} className="mr-1.5" /> {placingMode ? "Annuler placement" : "Placer un marqueur"}
          </Button>
          <Button
            data-testid="export-plan"
            variant="outline"
            onClick={async () => { try { toast.loading("Génération PDF..."); await exportPlanAsPdf(exportRef.current, `${plan.name.replace(/\s+/g, "-")}.pdf`); toast.dismiss(); toast.success("PDF téléchargé"); } catch (e) { toast.dismiss(); toast.error("Échec export"); } }}
          >
            <Download size={16} className="mr-1.5" /> Exporter PDF annoté
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[minmax(0,1fr)_320px] gap-4">
        <Card className="p-3 border-slate-200 min-w-0" ref={exportRef}>
          <div className={`relative rounded-lg ${placingMode ? "cursor-crosshair ring-2 ring-blue-400" : ""}`}>
            <PlanRenderer url={plan.file_url} alt={plan.name} containerRef={containerRef} onClick={handlePlanClick} onPageChange={setCurrentPage} className="select-none">
              {markersOverlay}
            </PlanRenderer>
          </div>
          {placingMode && (
            <div className="mt-2 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-md px-3 py-2 inline-flex items-center gap-1.5" data-testid="placing-hint">
              <MapPin size={12} /> Mode placement actif — cliquez sur la zone du plan pour choisir un désordre ou un sinistre à lier.
            </div>
          )}
        </Card>

        <div className="space-y-4">
          {/* Legend */}
          <Card className="p-4 border-slate-200" data-testid="plan-legend">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">Légende — {colorMode === "warranty" ? "Famille de garantie" : "Type de désordre"}</div>
            </div>
            {colorMode === "warranty" ? (
              <div className="space-y-1 text-xs">
                {WARRANTY_FAMILY_KEYS.map((k) => (
                  <button
                    key={k}
                    data-testid={`legend-warranty-${k}`}
                    onClick={() => setWarrantyFilter(warrantyFilter === k ? "all" : k)}
                    className={`w-full flex items-center gap-2 px-1.5 py-1 rounded-md text-left transition ${warrantyFilter === k ? "bg-slate-100" : "hover:bg-slate-50"}`}
                  >
                    <div className="w-3 h-3 rounded-full" style={{ background: WARRANTY_FAMILIES[k].color }} />
                    <span className="text-slate-700 flex-1">{WARRANTY_FAMILIES[k].full}</span>
                    {warrantyFilter === k && <span className="text-[10px] font-semibold text-slate-500">Filtré</span>}
                  </button>
                ))}
              </div>
            ) : (
              <div className="space-y-1.5 text-xs">
                {Object.entries(DEFECT_TYPE_COLORS).slice(0, 7).map(([k, c]) => (
                  <div key={k} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ background: c }} />
                    <span className="text-slate-700">{DEFECT_TYPES[k]}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Marker detail */}
          {selectedMarker && (() => {
            const linked = selectedMarker.linked_type === "defect" ? defectMap[selectedMarker.defect_id] : claimMap[selectedMarker.claim_id];
            const wk = linked?.warranty_type || "none";
            const wFamily = selectedMarker.linked_type === "defect" ? WARRANTY_FAMILIES[wk] : null;
            return (
              <Card className="p-4 border-slate-200" data-testid="marker-detail">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{selectedMarker.linked_type === "defect" ? "Désordre" : "Sinistre"}</div>
                    <div className="font-mono text-xs text-slate-500">{linked?.reference}</div>
                  </div>
                  <button onClick={() => setSelectedMarker(null)} className="p-1 hover:bg-slate-100 rounded"><X size={14} /></button>
                </div>
                <div className="font-semibold text-slate-900 mt-2">{linked?.title}</div>
                {wFamily && (
                  <div className="mt-2">
                    <span data-testid="marker-warranty-badge" className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: `${wFamily.color}15`, color: wFamily.color }}>
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: wFamily.color }} />{wFamily.full}
                    </span>
                  </div>
                )}
                {linked?.severity && <div className="mt-2"><Badge variant="outline" className={SEVERITY[linked.severity]?.color}>{SEVERITY[linked.severity]?.label}</Badge></div>}
                {linked?.status && <div className="mt-1.5 text-xs text-slate-700">{DEFECT_STATUS[linked.status]?.label || linked.status}</div>}
                {linked?.description && <p className="text-xs text-slate-600 mt-2">{linked.description}</p>}
                {linked?.estimated_cost > 0 && <div className="text-xs font-mono mt-2">{formatEUR(linked.estimated_cost)}</div>}
                <div className="text-[10px] text-slate-400 mt-2 inline-flex items-center gap-1"><Move size={10} /> Glisser-déposer le marqueur pour le repositionner.</div>
                <Button data-testid="delete-marker" variant="outline" size="sm" className="mt-3 w-full text-red-600 border-red-200" onClick={() => remove.mutate(selectedMarker.id)}>
                  <Trash2 size={13} className="mr-1.5" /> Supprimer le marqueur
                </Button>
              </Card>
            );
          })()}
        </div>
      </div>

      {/* Marker placement dialog */}
      <Dialog open={placeDialogOpen} onOpenChange={(o) => !o && closePlaceDialog()}>
        <DialogContent className="sm:max-w-lg" data-testid="place-marker-dialog">
          <DialogHeader><DialogTitle>Nouveau marqueur — sélectionnez l&apos;élément à lier</DialogTitle><DialogDescription>Choisissez un désordre existant, un sinistre, ou créez un désordre rapide pour ce marqueur.</DialogDescription></DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">Position enregistrée : x={pendingClick?.x?.toFixed(1)}% y={pendingClick?.y?.toFixed(1)}% (page {pendingClick?.page}).</p>
          <Tabs value={placeTab} onValueChange={setPlaceTab}>
            <TabsList className="grid grid-cols-3 w-full">
              <TabsTrigger value="defect" data-testid="place-tab-defect" disabled={assetDefects.length === 0}>Désordre ({assetDefects.length})</TabsTrigger>
              <TabsTrigger value="claim" data-testid="place-tab-claim" disabled={assetClaims.length === 0}>Sinistre ({assetClaims.length})</TabsTrigger>
              <TabsTrigger value="new" data-testid="place-tab-new">Nouveau désordre</TabsTrigger>
            </TabsList>
            <TabsContent value="defect" className="pt-3">
              {assetDefects.length === 0 ? (
                <div className="text-sm text-slate-500 py-6 text-center">Aucun désordre sur cet actif — utilisez l&apos;onglet « Nouveau désordre ».</div>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                  {assetDefects.map((d) => {
                    const wk = d.warranty_type || "none"; const w = WARRANTY_FAMILIES[wk];
                    return (
                      <button
                        key={d.id}
                        data-testid={`pick-defect-${d.id}`}
                        onClick={() => setPickDefectId(d.id)}
                        className={`w-full text-left p-3 rounded-lg border transition ${pickDefectId === d.id ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:bg-slate-50"}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="font-mono text-[10px] text-slate-500">{d.reference}</div>
                          <span className="inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: `${w.color}15`, color: w.color }}><span className="w-1.5 h-1.5 rounded-full" style={{ background: w.color }} />{w.label}</span>
                        </div>
                        <div className="text-sm font-semibold text-slate-900 mt-1">{d.title}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{DEFECT_TYPES[d.type]} • {DEFECT_STATUS[d.status]?.label}</div>
                      </button>
                    );
                  })}
                </div>
              )}
            </TabsContent>
            <TabsContent value="claim" className="pt-3">
              {assetClaims.length === 0 ? (
                <div className="text-sm text-slate-500 py-6 text-center">Aucun sinistre sur cet actif.</div>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                  {assetClaims.map((c) => (
                    <button
                      key={c.id}
                      data-testid={`pick-claim-${c.id}`}
                      onClick={() => setPickClaimId(c.id)}
                      className={`w-full text-left p-3 rounded-lg border transition ${pickClaimId === c.id ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:bg-slate-50"}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="font-mono text-[10px] text-slate-500">{c.reference}</div>
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${c.claim_family === "DO" ? "bg-rose-100 text-rose-800" : "bg-blue-100 text-blue-800"}`}>{c.claim_family}</span>
                      </div>
                      <div className="text-sm font-semibold text-slate-900 mt-1">{c.title}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{c.claim_type} • {c.status}</div>
                    </button>
                  ))}
                </div>
              )}
            </TabsContent>
            <TabsContent value="new" className="pt-3">
              <div className="space-y-3">
                <div><Label>Titre</Label><Input data-testid="quick-defect-title" value={quickForm.title} onChange={(e) => setQuickForm({ ...quickForm, title: e.target.value })} placeholder="Ex: Infiltration plafond salle 3" /></div>
                <div><Label>Description</Label><Textarea data-testid="quick-defect-desc" rows={2} value={quickForm.description} onChange={(e) => setQuickForm({ ...quickForm, description: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Type</Label>
                    <Select value={quickForm.type} onValueChange={(v) => setQuickForm({ ...quickForm, type: v })}>
                      <SelectTrigger data-testid="quick-defect-type"><SelectValue /></SelectTrigger>
                      <SelectContent>{Object.entries(DEFECT_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Gravité</Label>
                    <Select value={quickForm.severity} onValueChange={(v) => setQuickForm({ ...quickForm, severity: v })}>
                      <SelectTrigger data-testid="quick-defect-severity"><SelectValue /></SelectTrigger>
                      <SelectContent>{Object.entries(SEVERITY).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Garantie</Label>
                    <Select value={quickForm.warranty_type} onValueChange={(v) => setQuickForm({ ...quickForm, warranty_type: v })}>
                      <SelectTrigger data-testid="quick-defect-warranty"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Aucune</SelectItem>
                        {Object.entries(WARRANTY_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Coût estimé €</Label><Input type="number" data-testid="quick-defect-cost" value={quickForm.estimated_cost} onChange={(e) => setQuickForm({ ...quickForm, estimated_cost: Number(e.target.value) })} /></div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={closePlaceDialog} data-testid="place-cancel">Annuler</Button>
            <Button data-testid="place-confirm" className="text-white" style={{ background: "#023E73" }} onClick={confirmPlace} disabled={create.isPending || createDefectQuick.isPending}>
              {create.isPending || createDefectQuick.isPending ? "Placement..." : "Placer le marqueur"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
